import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Table, Typography, Spin, Result } from 'antd';
import dayjs from 'dayjs';
import { getPublicDebtShare } from '../../services/debt';
import { labelOfCollectionStatus, labelOfDebtType } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import './DebtSharePage.css';

const { Text } = Typography;

const DebtSharePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: res } = await getPublicDebtShare(token);
        setData(res || null);
      } catch (e) {
        setError(e?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  if (loading) {
    return (
      <div className="page-container p-share-debt">
        <div className="page-content page-content-wide">
          <div className="p-share-debt-loading">
            <Spin />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container p-share-debt">
        <div className="page-content page-content-wide">
          <Result status="error" title="无法打开公示页面" subTitle={error} />
        </div>
      </div>
    );
  }

  const debt = data?.debt || {};
  const personal = Array.isArray(data?.debtorsPersonal) ? data.debtorsPersonal : [];
  const enterprise = Array.isArray(data?.debtorsEnterprise) ? data.debtorsEnterprise : [];

  return (
    <div className="page-container p-share-debt">
      <div className="page-content page-content-wide space-y-md">
        <div className="space-y-sm">
          <div className="page-title">债权公示</div>
          {data?.expiresAt ? (
            <div>
              <Text type="secondary">链接有效期至：{dayjs(data.expiresAt).format('YYYY-MM-DD HH:mm')}</Text>
            </div>
          ) : null}
        </div>

      <Card className="card-compact">
        <Descriptions bordered size="small" column={1} title="基本信息">
          <Descriptions.Item label="债权编号">{debt.debtNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="债权名称">{debt.title || '-'}</Descriptions.Item>
          <Descriptions.Item label="债权类型">{labelOfDebtType(debt.debtType) || debt.debtType || '-'}</Descriptions.Item>
          <Descriptions.Item label="发生日">
            {debt.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="本金金额">{debt.principalAmount == null ? '-' : formatAmountText(debt.principalAmount)}</Descriptions.Item>
          <Descriptions.Item label="合计金额">{debt.totalAmount == null ? '-' : formatAmountText(debt.totalAmount)}</Descriptions.Item>
          <Descriptions.Item label="催收状态">{labelOfCollectionStatus(debt.collectionStatus) || debt.collectionStatus || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="card-compact" title="债务人（个人，已脱敏）">
        <Table
          className="p-share-debt-table"
          size="small"
          rowKey={(_, idx) => `p-${idx}`}
          dataSource={personal}
          pagination={false}
          tableLayout="auto"
          scroll={{ x: 'max-content' }}
          columns={[
            { title: '姓名', dataIndex: 'name', key: 'name', width: 140, ellipsis: true, render: (v) => v || '-' },
            { title: '手机号', dataIndex: 'phone', key: 'phone', width: 140, ellipsis: true, render: (v) => v || '-' },
            { title: '邮箱', dataIndex: 'email', key: 'email', width: 220, ellipsis: true, render: (v) => v || '-' },
            { title: '身份证号', dataIndex: 'idNo', key: 'idNo', width: 200, ellipsis: true, render: (v) => v || '-' },
            {
              title: '地区',
              key: 'area',
              width: 160,
              ellipsis: true,
              render: (_, row) => (
                <span className="p-share-debt-cell">
                  {[row?.province, row?.city].filter(Boolean).join(' ') || '-'}
                </span>
              ),
            },
          ]}
        />
      </Card>

      <Card className="card-compact" title="债务人（企业，已脱敏）">
        <Table
          className="p-share-debt-table"
          size="small"
          rowKey={(_, idx) => `e-${idx}`}
          dataSource={enterprise}
          pagination={false}
          tableLayout="auto"
          scroll={{ x: 'max-content' }}
          columns={[
            { title: '企业名称', dataIndex: 'companyName', key: 'companyName', width: 240, ellipsis: true, render: (v) => v || '-' },
            { title: '统一社会信用代码', dataIndex: 'socialCreditCode', key: 'socialCreditCode', width: 220, ellipsis: true, render: (v) => v || '-' },
            { title: '法定代表人', dataIndex: 'legalPersonName', key: 'legalPersonName', width: 120, ellipsis: true, render: (v) => v || '-' },
            { title: '法定代表人证件号', dataIndex: 'legalPersonIdNo', key: 'legalPersonIdNo', width: 200, ellipsis: true, render: (v) => v || '-' },
            { title: '联系电话', dataIndex: 'phone', key: 'phone', width: 160, ellipsis: true, render: (v) => v || '-' },
            { title: '邮箱', dataIndex: 'email', key: 'email', width: 220, ellipsis: true, render: (v) => v || '-' },
            {
              title: '地区',
              key: 'area',
              width: 160,
              ellipsis: true,
              render: (_, row) => (
                <span className="p-share-debt-cell">
                  {[row?.province, row?.city].filter(Boolean).join(' ') || '-'}
                </span>
              ),
            },
          ]}
        />
      </Card>
      </div>
    </div>
  );
};

export default DebtSharePage;
